<template>
  <main id="magazzino" class="page-container stock-list">
    <div class="table-container-stock">
      <div class="magazzino-filters">
        <input
          v-model="filterSearch"
          class="filter-input filter-input--search"
          placeholder="Cerca per codice, nome, brand o formato…"
          type="text"
        />
        <StatusSelect
          v-model="filterCategory"
          :options="opzioniCategoria"
          :disabled="isLoading"
          placeholder="Tutte le categorie"
          placeholderIcon="fa-layer-group"
        />
        <label class="filter-checkbox">
          <input
            type="checkbox"
            v-model="filterInStock"
            :disabled="isLoading"
          />
          Disponibili in magazzino
        </label>
        <button
          class="refresh-btn"
          :class="{ refreshed }"
          @click="handleRefresh"
          :disabled="refreshing || isLoading"
          title="Aggiorna"
          style="margin-left: auto"
        >
          <i
            :class="[
              'fa-solid',
              refreshed ? 'fa-check' : 'fa-arrows-rotate',
              { 'fa-spin': refreshing },
            ]"
          ></i>
          <span>{{
            refreshing ? "Aggiorna…" : refreshed ? "Aggiornato!" : "Aggiorna"
          }}</span>
        </button>
      </div>

      <div class="stock-table-region">
        <div class="tbl-wrap" :class="{ dimmed: isLoading }">
          <table class="tbl item-table">
            <thead>
              <tr>
                <th>Codice</th>
                <th>Nome</th>
                <th>Brand</th>
                <th v-if="!filterCategory">Categoria</th>
                <th class="col-giacenza">Giacenza</th>
                <th v-if="showBoxCol">{{ boxColLabel }}</th>
                <th v-if="showSqmCol">M² tot.</th>
                <th v-if="showMlCol">ML tot.</th>
                <th style="text-align: left">Azioni</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in filteredItems"
                :key="item.id"
                class="item-row"
                :class="{ 'item-row--critical': criticalItemIds[item.id] }"
              >
                <td class="td-name" :title="item.supplierCode || item.itemCode">
                  {{ item.supplierCode || item.itemCode }}
                </td>
                <td class="td-name" :title="item.itemName">
                  {{ item.itemName }}
                </td>
                <td>{{ item.brand ?? "—" }}</td>
                <td v-if="!filterCategory">{{ item.category }}</td>
                <td class="col-giacenza">
                  {{ item.warehouse?.inWarehouse ?? 0 }}
                  <span
                    v-if="
                      !['area', 'linear'].includes(itemGroup(item)) &&
                      item.totalDamagedQty > 0
                    "
                    class="damaged-badge"
                    :title="`${item.totalDamagedQty} pz danneggiati su carichi merce ricevuti`"
                  >
                    <i class="fa-solid fa-triangle-exclamation"></i>
                    {{ item.totalDamagedQty }}
                  </span>
                </td>
                <td v-if="showBoxCol">
                  {{
                    ["area", "linear"].includes(itemGroup(item))
                      ? item.warehouse?.boxQuantity ?? "—"
                      : "—"
                  }}
                  <span
                    v-if="
                      ['area', 'linear'].includes(itemGroup(item)) &&
                      item.totalDamagedQty > 0
                    "
                    class="damaged-badge"
                    :title="`${item.totalDamagedQty} sc. danneggiate su carichi merce ricevuti`"
                  >
                    <i class="fa-solid fa-triangle-exclamation"></i>
                    {{ item.totalDamagedQty }}
                  </span>
                </td>
                <td v-if="showSqmCol">
                  {{
                    itemGroup(item) === "area" &&
                    Number(item.warehouse?.sqmTotal) > 0
                      ? formatDecimal(item.warehouse.sqmTotal)
                      : "—"
                  }}
                </td>
                <td v-if="showMlCol">
                  {{
                    itemGroup(item) === "linear" &&
                    Number(item.warehouse?.mlTotal) > 0
                      ? formatDecimal(item.warehouse.mlTotal)
                      : "—"
                  }}
                </td>
                <td class="actions-cell">
                  <button
                    v-if="can('magazzino', 'M')"
                    :disabled="isReadOnly"
                    class="icons-button"
                    @click="openEdit(item)"
                    title="Modifica magazzino"
                    type="button"
                  >
                    <i class="fa-regular fa-pen-to-square"></i>
                  </button>
                  <button
                    class="icons-button"
                    @click="openHistory(item)"
                    title="Storico movimenti"
                    type="button"
                  >
                    <i class="fa-regular fa-clock"></i>
                  </button>
                  <button
                    v-if="can('magazzino', 'M')"
                    :disabled="isReadOnly"
                    class="icons-button"
                    :class="{
                      'btn-critical--active': criticalItemIds[item.id],
                    }"
                    @click="toggleCritical(item.id)"
                    title="Segnala criticità"
                    type="button"
                  >
                    <i class="fa-solid fa-triangle-exclamation"></i>
                  </button>
                </td>
              </tr>
              <!-- [sc-48351] Lo stato vuoto dei Listini, anche qui. Mentre
                   carica non si dice niente: sarebbe un vuoto che non c'è. -->
              <tr v-if="filteredItems.length === 0 && !isLoading">
                <td :colspan="colCount" class="no-result">
                  <EmptyState
                    v-if="filtriAttivi"
                    icon="fa-warehouse"
                    title="Nessun articolo corrisponde ai filtri"
                    :text="testoFiltriAttivi"
                  >
                    <button class="empty-state__reset" @click="azzeraFiltri">
                      Azzera i filtri
                    </button>
                  </EmptyState>
                  <EmptyState
                    v-else
                    icon="fa-warehouse"
                    title="Il magazzino è ancora vuoto"
                    text="Qui trovi le giacenze degli articoli dei tuoi listini. Aggiungi gli articoli nei listini e aggiorna le quantità quando arriva la merce."
                  >
                    <router-link to="/home/listini" class="empty-state__reset">
                      Vai ai listini
                    </router-link>
                  </EmptyState>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- LOADER OVERLAY (limitato alla sola regione tabella) -->
        <div
          v-if="isLoading"
          class="loader-overlay"
          aria-live="polite"
          aria-busy="true"
        >
          <div class="spinner" role="status"></div>
          <p>Carico il magazzino…</p>
        </div>
      </div>

      <div class="pagination">
        <div class="pagination-left"></div>
        <div class="pagination-right">
          <div class="pagination-info">
            {{ testoConteggio(pagination) }}
          </div>
          <div class="pagination-controls">
            <button
              class="icons-button"
              @click="goToPage(1)"
              :disabled="isLoading || pagination.currentPage === 1"
            >
              <i class="fa-solid fa-angles-left"></i>
            </button>
            <button
              class="icons-button"
              @click="goToPage(pagination.currentPage - 1)"
              :disabled="isLoading || pagination.currentPage === 1"
            >
              <i class="fa-solid fa-chevron-left"></i>
            </button>
            <button
              v-for="pageNum in visiblePages"
              :key="pageNum"
              @click="goToPage(pageNum)"
              :class="{ active: pagination.currentPage === pageNum }"
              class="pages-button"
            >
              {{ pageNum }}
            </button>
            <button
              class="icons-button"
              @click="goToPage(pagination.currentPage + 1)"
              :disabled="
                isLoading || pagination.currentPage === pagination.totalPages
              "
            >
              <i class="fa-solid fa-chevron-right"></i>
            </button>
            <button
              class="icons-button"
              @click="goToPage(pagination.totalPages)"
              :disabled="
                isLoading || pagination.currentPage === pagination.totalPages
              "
            >
              <i class="fa-solid fa-angles-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal modifica magazzino -->
    <div
      v-if="editItem"
      class="warehouse-modal-backdrop"
      @click.self="closeEdit"
    >
      <div class="warehouse-modal">
        <button
          class="icons-button warehouse-modal__close"
          @click="closeEdit"
          type="button"
        >
          <i class="fa-solid fa-xmark"></i>
        </button>
        <h3 class="warehouse-modal__title">Aggiorna magazzino</h3>
        <p class="warehouse-modal__subtitle">{{ editItem.itemName }}</p>

        <div class="warehouse-modal__fields">
          <template v-if="editGroup === 'area'">
            <!-- Articoli senza sqmPerBox (es. parquet senza dati imballo): input diretto m² -->
            <div v-if="editUsesDirectSqm" class="field">
              <label>M² in magazzino</label>
              <input
                type="number"
                min="0"
                step="0.01"
                v-model.number="editFields.sqmTotal"
              />
              <small>Inserisci i m² totali disponibili in magazzino</small>
            </div>
            <!-- Articoli con sqmPerBox (es. piastrelle): calcolo automatico da scatole -->
            <div v-else class="field">
              <label>Numero scatole</label>
              <input
                type="number"
                min="0"
                v-model.number="editFields.boxQuantity"
              />
              <small>Pezzi e m² calcolati automaticamente</small>
            </div>
          </template>
          <template v-else-if="editGroup === 'linear'">
            <div class="field">
              <label>Numero confezioni</label>
              <input
                type="number"
                min="0"
                v-model.number="editFields.boxQuantity"
              />
              <small>Metri lineari calcolati automaticamente</small>
            </div>
          </template>
          <template v-else>
            <div class="field">
              <label>Pezzi in magazzino</label>
              <input
                type="number"
                min="0"
                v-model.number="editFields.inWarehouse"
              />
            </div>
          </template>
        </div>

        <div class="warehouse-modal__actions">
          <button class="btn btn-secondary" @click="closeEdit" type="button">
            Annulla
          </button>
          <button
            class="btn btn-primary"
            @click="saveEdit"
            type="button"
            :disabled="isReadOnly || editSaving"
          >
            {{ editSaving ? "Salvataggio…" : "Salva" }}
          </button>
        </div>
      </div>
    </div>

    <ItemHistoryModal
      v-if="historyItem"
      :item-id="historyItem.id"
      :item-name="historyItem.name"
      :category="historyItem.category"
      :show="!!historyItem"
      @update:show="closeHistory"
    />
  </main>
</template>

<script>
import EmptyState from "@/components/EmptyState.vue";
import StatusSelect from "@/components/StatusSelect.vue";
import { warehouseCategoriesFor } from "@/config/catalog";
import { testoConteggio } from "@/utilities/conteggioPagine";
import axiosInstance from "@/axiosInstance";
import ItemHistoryModal from "../components/ItemHistoryModal.vue";
import { formatDecimal } from "../utilities/formatting";
import { trackEvent, EVENTS } from "@/utilities/analytics";

const COLUMN_GROUPS = {
  area: [
    "piastrelle",
    "rivestimenti parete",
    "parquet",
    "laminati",
    "decking esterno",
  ],
  linear: ["profili", "sistemi di scarico"],
  piece: ["sanitari", "rubinetteria", "box doccia", "accessori bagno"],
  furniture: ["mobili", "porte e infissi"],
};

function getCategoryGroup(category, salesUnit) {
  if (category === "piastrelle" && salesUnit === "ML") return "linear";
  for (const [group, cats] of Object.entries(COLUMN_GROUPS)) {
    if (cats.includes(category)) return group;
  }
  return "generic";
}

export default {
  name: "MagazzinoPage",
  components: { ItemHistoryModal, EmptyState, StatusSelect },
  data() {
    return {
      isLoading: false,
      refreshing: false,
      refreshed: false,
      items: [],
      pagination: {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 8,
        startItem: 1,
        endItem: 0,
      },
      filterSearch: "",
      filterCategory: "",
      filterInStock: false,
      historyItem: null,
      editItem: null,
      editFields: { boxQuantity: 0, inWarehouse: 0, sqmTotal: 0 },
      editSaving: false,
      criticalItemIds: {},
    };
  },
  computed: {
    // [sc-48351 / task 48786] I bottoni seguono il ruolo: nascosti, non
    // spenti — un bottone grigio senza spiegazione sembra un guasto.
    can() {
      return this.$store.getters["auth/can"];
    },
    filteredItems() {
      return this.items;
    },
    /**
     * [sc-48351] Le categorie della tendina: dalla configurazione e dagli
     * articoli che ci sono, non più da un elenco scritto a mano.
     */
    /** La tendina della piattaforma: «Tutte le categorie» e poi le categorie. */
    opzioniCategoria() {
      return [
        { value: "", label: "Tutte le categorie" },
        ...this.CATEGORIES.map((cat) => ({ value: cat, label: cat })),
      ];
    },
    CATEGORIES() {
      const branding = this.$store.getters["company/branding"];
      return warehouseCategoriesFor(
        branding?.productCategories,
        branding?.itemCategoriesInUse
      );
    },
    /** [sc-48351] C'è almeno un filtro che può nascondere articoli. */
    filtriAttivi() {
      return Boolean(
        this.filterSearch.trim() || this.filterCategory || this.filterInStock
      );
    },
    /** Quali filtri stanno nascondendo tutto, detti per esteso. */
    testoFiltriAttivi() {
      const filtri = [];
      const cerca = this.filterSearch.trim();
      if (cerca) filtri.push(`la ricerca «${cerca}»`);
      if (this.filterCategory)
        filtri.push(`la categoria «${this.filterCategory}»`);
      if (this.filterInStock) filtri.push("solo i disponibili in magazzino");
      return `Nessun risultato con ${filtri.join(
        ", "
      )}. Cambia i filtri, oppure azzerali per rivedere tutto il magazzino.`;
    },
    activeGroup() {
      return this.filterCategory ? getCategoryGroup(this.filterCategory) : null;
    },
    showBoxCol() {
      return (
        !this.activeGroup ||
        this.activeGroup === "area" ||
        this.activeGroup === "linear"
      );
    },
    showSqmCol() {
      return !this.activeGroup || this.activeGroup === "area";
    },
    showMlCol() {
      return (
        !this.activeGroup ||
        this.activeGroup === "linear" ||
        this.filterCategory === "piastrelle"
      );
    },
    boxColLabel() {
      return this.activeGroup === "linear"
        ? "N. Confezioni in stock"
        : "N. Scatole in stock";
    },
    colCount() {
      let n = 5; // Codice, Nome, Brand, Giacenza, actions
      if (!this.filterCategory) n++; // Categoria
      if (this.showBoxCol) n++;
      if (this.showSqmCol) n++;
      if (this.showMlCol) n++;
      return n;
    },
    editGroup() {
      return getCategoryGroup(
        this.editItem?.category,
        this.editItem?.salesUnit
      );
    },
    // True quando l'articolo è di tipo area ma non ha sqmPerBox configurato
    // (es. parquet senza dati imballo): in questo caso si inseriscono i m² direttamente.
    editUsesDirectSqm() {
      return (
        this.editGroup === "area" && !(Number(this.editItem?.sqmPerBox) > 0)
      );
    },
    visiblePages() {
      const range = 2;
      const start = Math.max(1, this.pagination.currentPage - range);
      const end = Math.min(
        this.pagination.totalPages,
        this.pagination.currentPage + range
      );
      return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    },
  },
  methods: {
    /** [sc-48351] Tutti i filtri a zero: i watcher ricaricano l'elenco. */
    azzeraFiltri() {
      this.filterSearch = "";
      this.filterCategory = "";
      this.filterInStock = false;
    },
    testoConteggio,
    formatDecimal,
    async handleRefresh() {
      this.refreshing = true;
      this.refreshed = false;
      try {
        await this.loadItems(this.pagination.currentPage);
      } finally {
        this.refreshing = false;
        this.refreshed = true;
        setTimeout(() => {
          this.refreshed = false;
        }, 1500);
      }
    },

    async toggleCritical(id) {
      try {
        const { data } = await axiosInstance.patch(`/item/${id}/critical`);
        this.criticalItemIds = {
          ...this.criticalItemIds,
          [id]: data.isCritical,
        };
      } catch {
        this.$toast.error("Errore durante l'aggiornamento della criticità.");
      }
    },
    itemGroup(item) {
      return getCategoryGroup(item.category, item.salesUnit);
    },
    openHistory(item) {
      this.historyItem = {
        id: item.id,
        name: item.itemName,
        category: item.category,
      };
    },
    closeHistory() {
      this.historyItem = null;
    },

    openEdit(item) {
      this.editItem = item;
      const wh = item.warehouse ?? {};
      this.editFields = {
        boxQuantity: wh.boxQuantity ?? 0,
        inWarehouse: wh.inWarehouse ?? 0,
        sqmTotal: wh.sqmTotal ? Number(wh.sqmTotal) : 0,
      };
    },
    closeEdit() {
      this.editItem = null;
      this.editSaving = false;
    },
    async saveEdit() {
      this.editSaving = true;
      try {
        const group = this.editGroup;
        const payload = {};
        if (group === "area") {
          if (this.editUsesDirectSqm) {
            payload.sqmTotal = this.editFields.sqmTotal;
          } else {
            payload.boxQuantity = this.editFields.boxQuantity;
          }
        } else if (group === "linear") {
          payload.boxQuantity = this.editFields.boxQuantity;
        } else {
          payload.inWarehouse = this.editFields.inWarehouse;
        }
        await axiosInstance.patch(`/item/update/${this.editItem.id}`, payload);
        this.$toast.success("Magazzino aggiornato.");
        this.closeEdit();
        this.loadItems(this.pagination.currentPage);
      } catch (err) {
        const msg =
          err.response?.data?.message ||
          "Errore durante l'aggiornamento del magazzino.";
        this.$toast.warning(msg);
      } finally {
        this.editSaving = false;
      }
    },

    async loadItems(page = 1) {
      this.isLoading = true;
      try {
        const { data } = await axiosInstance.get("/item/get-all", {
          params: {
            page,
            limit: 8,
            ...(this.filterCategory ? { category: this.filterCategory } : {}),
            ...(this.filterSearch ? { search: this.filterSearch } : {}),
            ...(this.filterInStock ? { inStock: true } : {}),
          },
        });
        this.items = Array.isArray(data.results) ? data.results : [];

        // Ricerca articoli: viene tracciato solo il fatto che una ricerca è
        // avvenuta e se ha prodotto risultati. Il testo cercato NON viene
        // inviato (potrebbe contenere qualsiasi cosa digiti l'utente).
        if (this.filterSearch) {
          trackEvent(EVENTS.ARTICLE_SEARCHED, {
            action: "search",
            kind: "item",
            outcome: this.items.length ? "results" : "empty",
          });
        }
        const newCritical = {};
        this.items.forEach((item) => {
          if (item.warehouse?.isCritical) newCritical[item.id] = true;
        });
        this.criticalItemIds = { ...this.criticalItemIds, ...newCritical };
        const p = data.pagination;
        this.pagination = {
          currentPage: p.currentPage,
          totalPages: p.totalPages,
          totalItems: p.totalItems,
          itemsPerPage: p.itemsPerPage,
          startItem: (p.currentPage - 1) * p.itemsPerPage + 1,
          endItem: Math.min(p.currentPage * p.itemsPerPage, p.totalItems),
        };
      } catch (err) {
        if (err.response?.status === 404) {
          this.items = [];
          return;
        }
        this.$toast.error("Errore durante il caricamento del magazzino.");
      } finally {
        this.isLoading = false;
      }
    },

    goToPage(page) {
      if (this.isLoading) return;
      if (page < 1 || page > this.pagination.totalPages) return;
      this.loadItems(page);
    },
  },
  mounted() {
    this.loadItems(1);
  },
  watch: {
    filterCategory() {
      this.loadItems(1);
    },
    filterSearch() {
      this.loadItems(1);
    },
    filterInStock() {
      this.loadItems(1);
    },
  },
};
</script>

<style src="@/css/StockPage.css" />
<style scoped>
.warehouse-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.warehouse-modal {
  background: #fff;
  border-radius: 8px;
  padding: 28px 32px;
  min-width: 320px;
  max-width: 420px;
  width: 100%;
  position: relative;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
}
.warehouse-modal__close {
  position: absolute;
  top: 12px;
  right: 12px;
}
.warehouse-modal__title {
  margin: 0 0 4px;
  font-size: 1.1rem;
}
.warehouse-modal__subtitle {
  margin: 0 0 20px;
  color: #666;
  font-size: 0.9rem;
}
.warehouse-modal__fields {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 24px;
}
.warehouse-modal__fields .field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.warehouse-modal__fields label {
  font-size: 0.85rem;
  font-weight: 600;
}
.warehouse-modal__fields input {
  padding: 8px 10px;
  border: 0.5px solid #ccc;
  border-radius: 4px;
  font-size: 1rem;
}
.warehouse-modal__fields small {
  font-size: 0.78rem;
  color: #888;
}
.col-giacenza {
  background-color: #d6f3ff;
  font-weight: 600;
}
.item-row--critical {
  background-color: #fff8e1 !important;
}
.item-row--critical .col-giacenza {
  background-color: #fff3cd !important;
}
.item-row--critical:hover {
  background-color: #fdefc3 !important;
}
.btn-critical--active {
  color: #e65100;
}
.damaged-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 999px;
  background: #fef2f2;
  border: 0.5px solid #fca5a5;
  color: #dc2626;
  font: 600 11px "Inter", sans-serif;
  white-space: nowrap;
  cursor: default;
}
.item-row:hover .col-giacenza {
  background-color: #e0e0e0;
}
.warehouse-modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
</style>
